import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import type { StatsPeriod } from '@/api/statsApi';
import { useClient } from '@/clients/useClient';
import { MapView } from '@/components/map/components/MapView';
import { validateMapSearch } from '@/components/map/utils/mapUrlState';
import { StatsBar } from '@/components/stats/StatsBar';
import { StatsConfigPanel } from '@/components/stats/StatsConfigPanel';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useNetworkExplorerStats } from '@/hooks/useNetworkExplorerStats';
import { useStatsPreferences } from '@/hooks/useStatsPreferences';
import { MapInteractionProvider } from '@/map-interaction/MapInteractionProvider';
import { ExclusivePanelProvider } from '@/panels/ExclusivePanelProvider';
import { useExclusivePanel } from '@/panels/useExclusivePanel';
import { StatsBarSlotProvider } from '@/stats/StatsBarSlotContext';
import { StatsFiltersProvider } from '@/stats/StatsFiltersProvider';
import { useMobileBottomSlot } from '@/stats/useMobileBottomSlot';

export const Route = createFileRoute('/network-explorer')({
    validateSearch: validateMapSearch,
    component: NetworkExplorerPage,
});

/**
 * Network Explorer: the cross-client globe for authorised Origin staff. A viewing mode, not a
 * product — it lives outside ProductGuard, so the client selected underneath stays exactly as
 * it was and choosing a client from the switcher simply returns to it. The capability gate
 * here is UX only; every explorer endpoint re-checks the permission server-side.
 */
function NetworkExplorerPage() {
    const { canViewNetworkExplorer, isLoading } = useClient();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isLoading && !canViewNetworkExplorer) void navigate({ to: '/', replace: true });
    }, [isLoading, canViewNetworkExplorer, navigate]);

    // The app-level loader covers bootstrap; an unauthorised user is on their way to `/`.
    if (isLoading || !canViewNetworkExplorer) return null;

    // The explorer map is interactive (pinned cluster card, panels, filters), so it gets the
    // real providers; the stats filters bridge carries the explorer's committed filters from
    // its Filters panel up to the header figures, as it does in Find & Fix. Stats scope, map
    // actions and the table are genuinely absent — there is no AOI/DMA scoping, no out-of-tree
    // reset control and no table across the estate — so their no-op fallbacks remain correct.
    return (
        <ExclusivePanelProvider>
            <StatsFiltersProvider>
                <MapInteractionProvider>
                    <NetworkExplorerContent />
                </MapInteractionProvider>
            </StatsFiltersProvider>
        </ExclusivePanelProvider>
    );
}

function NetworkExplorerContent() {
    // Stats is one of the mutually-exclusive side panels, so opening Layers closes it and
    // vice versa — the same arrangement as Find & Fix.
    const { activePanel, setActivePanel } = useExclusivePanel();
    const isStatsPanelOpen = activePanel === 'stats';
    const [period, setPeriod] = useState<StatsPeriod>('overall');
    const stats = useNetworkExplorerStats(period);
    // The explorer's own set, kept apart from every client's Find & Fix bar: a set chosen for
    // an estate-wide audience must not change what the user sees in a client. Defaults to the
    // agreed six.
    const statsPreferences = useStatsPreferences('explorer');
    const isDesktop = useMediaQuery('(min-width: 768px)');
    const mobileSlot = useMobileBottomSlot();

    const statsBar = (
        <StatsBar
            data={stats.data}
            isLoading={stats.isLoading}
            isError={stats.isError}
            visibleStats={statsPreferences.visibleStats}
            onOpenConfig={() => {
                setActivePanel(isStatsPanelOpen ? null : 'stats');
            }}
        />
    );

    return (
        // Desktop: hand the bar to MapView via context so it renders above the map only, and
        // the config panel sits full-height beside the [bar + map] column.
        // Mobile: portal the bar into the layout-level slot below the header instead.
        <StatsBarSlotProvider value={isDesktop ? statsBar : null}>
            <div className="absolute inset-0 flex flex-col overflow-hidden">
                <div className="flex min-h-0 flex-1">
                    {/* The map is taken out of flow, as the Find & Fix page does: the bar's stat
                        items never shrink, so an in-flow map column would grow with a long bar
                        and push the panel off screen instead of letting the bar scroll. */}
                    <div className="relative min-h-0 min-w-0 flex-1">
                        <div className="absolute inset-0 overflow-hidden">
                            <MapView mode="network-explorer" />
                        </div>
                    </div>
                    <StatsConfigPanel
                        scope="explorer"
                        isOpen={isStatsPanelOpen}
                        visibleStats={statsPreferences.visibleStats}
                        onToggle={statsPreferences.toggleStat}
                        onClose={() => {
                            setActivePanel(null);
                        }}
                        period={period}
                        onPeriodChange={setPeriod}
                    />
                </div>
                {!isDesktop && mobileSlot && createPortal(statsBar, mobileSlot)}
            </div>
        </StatsBarSlotProvider>
    );
}
